import { TableCell, TableRow, TextField, IconButton } from "@mui/material";
import { ChevronDown, ChevronRight } from "lucide-react";
import { BudgetRowProps } from "./types";
import { formatNumber } from "../../utils/format";
import { Trash2, Eraser } from "lucide-react";
import { Body } from "../ui/Typography";

export const BudgetRow = ({
  row,
  months,
  changedFields,
  onAmountChange,
  isGroupCollapsed,
  onToggleGroup,
  onDeleteRow,
  onClearRow
}: BudgetRowProps) => {
  const isEditable = !row.isSum && !row.isGroupTotal;
  const fontWeight = row.isGroupTotal ? 'font-bold' : 'font-normal';

  if (row.groupKey && !row.isGroupTotal && isGroupCollapsed[row.groupKey]) {
    return null;
  }

  const rowSumZero = Object.values(row.amounts).every(amount => amount === 0);

  return (
    <TableRow className={`${fontWeight}`}>
      <TableCell
        className={`sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]`}
        sx={{
          fontSize: '0.875rem', // Adjusted font size
          borderBottom: 'none',
          borderRight: '1px solid rgba(224, 224, 224, 1)',
          backgroundColor: 'background.default',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'inherit',
            zIndex: -1,
          }
        }}
      >
        {row.isGroupTotal && (
          <div onClick={() => onToggleGroup(row.groupKey!)}
            className="flex items-center gap-2 cursor-pointer" >
            <button
              className="p-1 hover:bg-gray-200 rounded"
            >
              {isGroupCollapsed[row.groupKey!] ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            <Body>{row.number || row.name}</Body>
          </div>
        )}
        {!row.isGroupTotal && (
          <div className="flex items-center justify-between gap-2 group" >
            <Body>{row.number || row.name}</Body>
            <IconButton
              // error if all row.amounts is zero
              color={rowSumZero ? 'error' : 'default'}
              className="ml-8 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              onClick={() => rowSumZero ? onDeleteRow(row.id) : onClearRow(row.id)}
            >
              {rowSumZero ? <Trash2 /> : <Eraser />}
            </IconButton>
          </div>
        )}
      </TableCell>
      <TableCell
        sx={{
          fontSize: '0.875rem', // Adjusted font size
          borderBottom: 'none',
          borderRight: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Body>{row.description}</Body>
      </TableCell>
      {months.map((monthStr) => (
        <TableCell
          key={monthStr}
          align="right"
          style={{ minWidth: 150 }}
          sx={{
            borderBottom: 'none',
            borderRight: '1px solid rgba(224, 224, 224, 1)',
            '&:last-child': {
              borderRight: 'none'
            }
          }}
        >
          {isEditable ? (
            <TextField
              value={formatNumber(row.amounts[monthStr] || 0)}
              onChange={(e) => onAmountChange(row.id, monthStr, e.target.value)}
              variant="standard"
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  '& .MuiInput-input': {
                    textAlign: 'right',
                  },
                  ...(changedFields.has(`${row.id}-${monthStr}`) && {
                    '& .MuiInput-input': {
                      border: `2px solid`,
                      textAlign: 'right',
                      borderRadius: 1,
                    }
                  }),
                }
              }}
              fullWidth
            />
          ) : (
            <span style={{ fontSize: '0.875rem', fontFamily: 'monospace', color: "gray", cursor: 'default' }}>
              {formatNumber(row.amounts[monthStr] || 0)}
            </span>
          )}
        </TableCell>
      ))
      }
    </TableRow >
  );
};